import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Factory,
  Cog,
  FlaskConical,
  PackageCheck,
  ShieldCheck,
} from "lucide-react";
import img2 from "../assets/Stage 2.png";
import img3 from "../assets/stage 3.png";
import img4 from "../assets/Stage 4.png";
import img5 from "../assets/Stage 5.png";
const Manufacturing = () => {
  const stages = [
    {
      id: 1,
      title: "Stage 1: Pre-Processing",
      subtitle: "Raw Material to Powder Formation",
      image: img2,
      intro:
        "This stage focuses on converting raw chemical inputs into a process-ready intermediate form.",
      points: [
        "Raw Material Charging: Carefully selected dye intermediates and chemicals are loaded into large reaction vessels.",
        "Mixing & Churning: The materials undergo continuous agitation to ensure uniform blending and reaction consistency.",
        "Lump Formation (Wet Cake Stage): The mixture gradually transforms into a semi-solid, lumpy mass resembling a raw dye cake.",
        "Drying Process: The wet cake is dried under controlled conditions to remove moisture and stabilize the product.",
        "Coarse Grinding: The dried lumps are transferred to industrial mixers where they are broken down into coarse powder form.",
      ],
      note: "At this stage, the material is not yet suitable for final application and requires precision milling.",
    },

    {
      id: 2,
      title: "Stage 2: Fine Grinding",
      subtitle: "Ball Mill Process",
      image: img2,
      intro:
        "This is the most critical stage in achieving the final quality of the dye.",
      points: [
        "The coarse powder is fed into a high-capacity Ball Mill.",
        "Inside the rotating chamber, heavy-duty iron/steel balls create constant impact and friction.",
        "This mechanical action breaks down particles into ultra-fine, uniform powder.",
      ],
      subHeading: "The process ensures:",
      subPoints: [
        "Consistent particle size distribution",
        "Improved color strength",
        "Better solubility and dispersion in textile applications",
      ],
      note: "Precision in this stage directly impacts dye performance and customer satisfaction.",
    },

    {
      id: 3,
      title: "Stage 3: Quality Evaluation & Sample Dyeing",
      subtitle: "Shade Matching & Buyer Approval",
      image: img3,
      intro: "The finely milled dye is subjected to sample dyeing on fabric.",
      points: [
        "Shade, absorption, and consistency are visually and technically evaluated.",
        "Products are developed and adjusted strictly as per buyer specifications, ensuring exact shade and performance requirements.",
        "Multiple iterations may be performed to match precise client expectations.",
      ],
    },

    {
      id: 4,
      title: "Stage 4: Computerized Lab Testing & Analysis",
      subtitle: "Advanced Quality Control",
      image: img4,
      intro:
        "Advanced instruments like spectrophotometers are used for precise color measurement.",
      points: ["Testing is conducted as per buyer specifications, including:"],
      subPoints: ["Strength", "Tone", "Solubility"],
      secondHeading: "Data is analyzed using computerized systems to ensure:",
      secondPoints: [
        "Shade accuracy",
        "Batch consistency",
        "Compliance with international quality standards",
      ],
      note: "Detailed reports are generated and approved before moving forward.",
    },

    {
      id: 5,
      title: "Stage 5: Final Packing & Finished Goods",
      subtitle: "Packaging & Dispatch",
      image: img5,
      intro: "Approved dye batches are transferred to the packaging unit.",
      points: [
        "Packaging is carried out as per buyer specifications, including:",
      ],
      subPoints: [
        "HDPE bags",
        "Drums",
        "Corrugated boxes",
        "With or without pallets",
      ],
      secondHeading:
        "We also offer customized packaging solutions based on buyer requirements, including packing size, labeling, and handling preferences to suit specific market needs.",
      thirdHeading: "We ensure all packaging is:",
      thirdPoints: [
        "Moisture-proof",
        "Export-grade",
        "Safe for long-distance international shipping",
      ],
      fourthHeading: "Each batch is labeled with:",
      fourthPoints: [
        "Product details",
        "Batch number",
        "Quality specifications",
      ],
      note: "Finished goods are dispatched for both domestic and global markets with full export readiness.",
    },
  ];

  const aboutPoints = [
    "Founded in 2008 with a vision to deliver world-class dyes & chemicals.",
    "Serving textile, leather, paper, nylon, wool, silk and ink industries.",
    "Trusted for reliability, consistency and customized solutions.",
    "Environment-conscious production with modern technology.",
    "Led by experienced industry professionals with global market focus.",
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-r from-red-500 to-indigo-900 py-28 px-4">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm mb-6">
              <Factory className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
              Our Manufacturing Process
            </h1>

            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Precision manufacturing, strict quality control, and export-ready
              packaging built for global industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-4">
              <Building2 className="w-4 h-4" />
              About Us
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Trusted Dye Manufacturer Since 2008
            </h2>

            <p className="text-gray-600 text-lg mb-6 leading-8">
              Meet Enterprise is a leading manufacturer and exporter of premium
              quality dyes and chemicals. We combine technical expertise, modern
              machinery, and customer-first service to deliver consistent
              products for diverse industrial applications.
            </p>

            <div className="space-y-3">
              {aboutPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-green-600 mt-1" />
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src="https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1000&q=80"
              alt="Manufacturing Facility"
              className="w-full rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Manufacturing Process
            </h2>

            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A carefully controlled 5-stage production system ensuring
              consistency, quality, and export-ready results.
            </p>
          </div>

          <div className="space-y-10">
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {/* =====================
              STAGE 1 - NO IMAGE
          ====================== */}
                {stage.id === 1 ? (
                  <div className="lg:col-span-2 p-8 md:p-10">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg mb-5">
                      {stage.id}
                    </div>

                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {stage.title}
                    </h3>

                    <p className="text-purple-600 font-semibold mb-6 text-lg">
                      {stage.subtitle}
                    </p>

                    <p className="text-gray-700 mb-5 leading-8 text-lg">
                      {stage.intro}
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      {stage.points?.map((item, i) => (
                        <div
                          key={i}
                          className="p-4 rounded-xl bg-gray-50 border border-gray-100 flex gap-3"
                        >
                          <span className="text-purple-600 font-bold">✓</span>
                          <span className="text-gray-700 leading-7">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>

                    {stage.note && (
                      <div className="mt-6 p-4 rounded-xl bg-purple-50 border border-purple-100 text-gray-800 font-medium">
                        👉 {stage.note}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Image */}
                    <div className={`${index % 2 !== 0 ? "lg:order-2" : ""}`}>
                      <img
                        src={stage.image}
                        alt={stage.title}
                        className="w-full h-full min-h-[350px] object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div
                      className={`p-8 ${index % 2 !== 0 ? "lg:order-1" : ""}`}
                    >
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-600 text-white font-bold mb-4">
                        {stage.id}
                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {stage.title}
                      </h3>

                      <p className="text-purple-600 font-semibold mb-5">
                        {stage.subtitle}
                      </p>

                      <p className="text-gray-700 mb-4 leading-7">
                        {stage.intro}
                      </p>

                      {/* Main Points */}
                      <div className="space-y-3 mb-4">
                        {stage.points?.map((item, i) => (
                          <div key={i} className="flex gap-3">
                            <span className="text-purple-600 mt-1">•</span>
                            <span className="text-gray-700 leading-7">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Sub Heading */}
                      {stage.subHeading && (
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {stage.subHeading}
                        </h4>
                      )}

                      {/* Sub Points */}
                      {stage.subPoints && (
                        <div className="space-y-2 mb-4">
                          {stage.subPoints.map((item, i) => (
                            <div key={i} className="flex gap-3">
                              <span className="text-green-600 mt-1">✔</span>
                              <span className="text-gray-700">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {stage.note && (
                        <div className="mt-5 p-4 rounded-xl bg-purple-50 border border-purple-100 text-gray-800 font-medium">
                          👉 {stage.note}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-8 rounded-2xl bg-gray-50 text-center shadow-sm">
            <FlaskConical className="w-10 h-10 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-3">Lab Precision</h3>
            <p className="text-gray-600">
              Advanced testing instruments ensure exact tone, strength, and
              consistency in every lot.
            </p>
          </div>

          <div className="p-8 rounded-2xl bg-gray-50 text-center shadow-sm">
            <Factory className="w-10 h-10 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-3">Modern Production</h3>
            <p className="text-gray-600">
              Controlled processes and efficient machinery deliver scalable,
              reliable manufacturing output.
            </p>
          </div>

          <div className="p-8 rounded-2xl bg-gray-50 text-center shadow-sm">
            <PackageCheck className="w-10 h-10 text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-3">Export Ready</h3>
            <p className="text-gray-600">
              Secure packaging, proper labeling, and shipment readiness for
              domestic and global markets.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;
